'use client';

import React from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';

interface DeleteUserModalProps {
  userToDelete: { id: string; name: string; email: string } | null;
  onClose: () => void;
  confirmDeleteUser: () => void;
  deletingUserId: string | null;
}

export const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  userToDelete,
  onClose,
  confirmDeleteUser,
  deletingUserId,
}) => {
  if (!userToDelete) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-[#0A0D1A] border border-rose-500/30 rounded-3xl max-w-md w-full p-6 space-y-5 shadow-2xl animate-in fade-in zoom-in duration-200">
        <div className="flex items-center gap-3.5 border-b border-slate-800 pb-4">
          <div className="w-12 h-12 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-400 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-base font-black text-white">Permanently Delete User?</h3>
            <p className="text-xs text-rose-400/90 font-medium">Critical Administrative Action</p>
          </div>
        </div>

        <div className="bg-[#060811] border border-slate-800 rounded-2xl p-4 space-y-2">
          <p className="text-xs text-slate-300 leading-relaxed">
            Are you sure you want to permanently delete <strong className="text-white">{userToDelete.name}</strong> (<span className="text-indigo-400 font-mono">{userToDelete.email}</span>)?
          </p>
          <p className="text-[11px] text-slate-400">
            This will remove their account, workspace memberships, and permissions. <strong className="text-rose-400">This action cannot be undone.</strong>
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2.5 bg-[#060811] hover:bg-slate-800 border border-slate-800 rounded-xl text-xs font-bold text-slate-300 transition-all cursor-pointer"
          >
            Cancel
          </button>

          <button
            onClick={confirmDeleteUser}
            disabled={deletingUserId === userToDelete.id}
            className="px-5 py-2.5 bg-gradient-to-r from-rose-600 to-red-600 hover:opacity-90 disabled:opacity-50 text-white font-extrabold text-xs rounded-xl shadow-lg shadow-rose-600/30 flex items-center gap-2 transition-all cursor-pointer"
          >
            {deletingUserId === userToDelete.id ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                <span>Confirm Delete</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
